import { loadGoogleFont } from '../../lib/googleFonts';
import type { CommunityTextBox, FontCategory, FontRead } from '../../types/api';

/**
 * How a catalogue font becomes CSS, for the preview, the canvas and the
 * gallery. The browser draws the Google Fonts copy of the face the server
 * vendors, at the CSS weights the catalogue says match the renderer's files,
 * so the two agree on the face if not on every pixel of wrapping.
 */

/** The categories in the order the gallery's tabs list them. */
export const FONT_CATEGORIES: readonly FontCategory[] = [
  'sans',
  'serif',
  'display',
  'script',
  'mono',
  'rounded',
];

export const FONT_CATEGORY_LABELS: Record<FontCategory, string> = {
  sans: 'Sans',
  serif: 'Serif',
  display: 'Display',
  script: 'Script',
  mono: 'Mono',
  rounded: 'Rounded',
};

/** What the browser falls back to while the face loads, or if it never does. */
const CATEGORY_FALLBACKS: Record<FontCategory, string> = {
  sans: "system-ui, -apple-system, 'Segoe UI', sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  display: "Impact, 'Arial Narrow', sans-serif",
  script: "'Brush Script MT', cursive",
  mono: "ui-monospace, 'Courier New', monospace",
  rounded: 'ui-rounded, system-ui, sans-serif',
};

/** The sample a gallery tile shows when the box has no default text. */
export const FONT_SAMPLE_FALLBACK = 'The quick brown fox';

/** `'<name>', <category fallback>`. Quotes and backslashes are dropped from the name. */
export function fontFamilyCss(font: FontRead): string {
  const name = font.name.replace(/['"\\]/g, '');
  // The server's category is one of six; an unexpected one still gets a stack.
  const fallback = (CATEGORY_FALLBACKS as Partial<Record<string, string>>)[font.category];
  return `'${name}', ${fallback ?? 'sans-serif'}`;
}

/** The CSS weight that matches the file the renderer draws a box's weight in. */
export function fontWeightFor(font: FontRead, weight: CommunityTextBox['weight']): number {
  return weight === 'bold' ? font.bold_weight : font.regular_weight;
}

/** Asks for the face's stylesheet at both of its weights. Idempotent. */
export function loadCatalogFont(font: FontRead): void {
  loadGoogleFont(font.name, [font.regular_weight, font.bold_weight]);
}

/** A category said on screen; an unexpected one is shown as the server wrote it. */
export function fontCategoryLabel(category: string): string {
  return (FONT_CATEGORY_LABELS as Partial<Record<string, string>>)[category] ?? category;
}
